import { useEffect, useState, useSyncExternalStore } from 'react';
import { getCasinoRoundBets } from '../../apiservices/CasionApi';
import { getLastCasinoDetail, subscribeLastCasinoDetail } from '../../utils/lastCasinoDetail';
import { displayNetOdds } from '../../utils/casinoNetOdds';
import { formatOdds, formatAmount } from '../../utils/formatOdds';
import { formatBetNation, formatBetOdds } from '../../utils/casinoMyBets';
import { fmtDateTime } from '../StatementResultModal/mapCasinoBet';
import '../StatementResultModal/statementResult.css';

/**
 * Casino result modal: the round's DOWNLINE bets (ported from tenexch RoundBetsTable,
 * 16d39afb): All / Back / Lay / Deleted radios, Total Bets / Total Win, then
 * Username / Nation / Rate / Amount / Win / Date / IP / B Details / Action.
 * Ticked rows narrow the totals; none ticked = every visible row. The round is the
 * one whose detail the modal shows (utils/lastCasinoDetail). Hidden when the round
 * has no downline bets (VIP Roulette / Roulette keep radios + zero totals); when a
 * filter matches nothing only radios + totals stay.
 * Look: d99's own Details-modal styles (statementResult.css).
 */
const FILTERS = [['all', 'All'], ['back', 'Back'], ['lay', 'Lay'], ['deleted', 'Deleted']];
// VIP Roulette / Roulette keep the radios + "Total Bets: 0 / Total Win: 0" on a round with no
// downline bets; Beach Roulette shows only the result (tenexch d5d98dc4 / ea3cb0db / e30beb8e).
const EMPTY_BETS_BLOCK_GAMES = new Set(['roulette11', 'roulette13']);

const isLay = (b) => String(b.side || '').toLowerCase() === 'lay';
const signed = (n) => (n < 0 ? 'text-danger' : 'text-success');

export default function RoundBets({ show, roundId }) {
  const last = useSyncExternalStore(subscribeLastCasinoDetail, getLastCasinoDetail);
  const type = last?.type || '';
  const lastMid = last?.mid || '';
  // A widget that opened the modal without fetching detail leaves an older round in
  // the store — only use it when it is the round the modal shows.
  const rid = roundId == null ? '' : String(roundId).trim();
  const mid = !rid || rid.includes(lastMid) || lastMid.includes(rid) ? lastMid : '';
  const [bets, setBets] = useState([]);
  const [filter, setFilter] = useState('all');
  const [picked, setPicked] = useState(() => new Set());
  const [openBrowser, setOpenBrowser] = useState(null);

  useEffect(() => {
    setBets([]);
    setFilter('all');
    if (!show || !type || !/^\d+$/.test(mid)) return undefined;
    const ctrl = new AbortController();
    getCasinoRoundBets(type, mid, ctrl.signal).then((rows) => {
      if (!ctrl.signal.aborted) setBets(rows);
    });
    return () => ctrl.abort();
  }, [show, type, mid]);

  useEffect(() => { setPicked(new Set()); setOpenBrowser(null); }, [filter, bets]);

  if (!show || (bets.length === 0 && !(mid && EMPTY_BETS_BLOCK_GAMES.has(type)))) return null;

  // Deleted bets only under their own tab — All/Back/Lay leave them out
  const visible = bets.filter((b) => {
    if (filter === 'deleted') return b.deleted;
    if (b.deleted) return false;
    if (filter === 'all') return true;
    return filter === 'lay' ? isLay(b) : !isLay(b);
  });
  const counted = picked.size ? visible.filter((b) => picked.has(b.id)) : visible;
  const totalWin = Math.round(counted.reduce((sum, b) => sum + (Number(b.win) || 0), 0) * 100) / 100;
  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="round-bets">
      {/* no table → keep 8px under the radios (the reference's empty table area) */}
      <div className={`stmt-details-filters round-bets-head${visible.length ? '' : ' mb-2'}`}>
        <div>
          {FILTERS.map(([v, label]) => (
            <div className="form-check form-check-inline" key={v}>
              <input
                type="radio"
                className="form-check-input"
                id={`result-bet-${v}`}
                name="result-bet-filter"
                checked={filter === v}
                onChange={() => setFilter(v)}
              />
              <label className="form-check-label" htmlFor={`result-bet-${v}`}>{label}</label>
            </div>
          ))}
        </div>
        <div className="stmt-details-totals round-bets-totals">
          Total Bets: <span className="count">{counted.length}</span>
          &nbsp;&nbsp; Total Win:{' '}
          <span className={signed(totalWin)}>{formatAmount(totalWin)}</span>
        </div>
      </div>
      {visible.length > 0 && (
        <div className="table-responsive">
          <table className="table stmt-details-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Nation</th>
                <th>Rate</th>
                <th>Amount</th>
                <th>Win</th>
                <th>Date</th>
                <th>IP</th>
                <th>B Details</th>
                <th className="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((b) => {
                const win = Number(b.win);
                return [
                  <tr className={isLay(b) ? 'lay' : 'back'} key={b.id}>
                    <td>{b.username}</td>
                    <td>{formatBetNation(type, b.nation, b)}</td>
                    <td>{formatOdds(formatBetOdds(type, b, displayNetOdds(type, b.rate), last?.response?.data?.data?.t1 || last?.response?.data?.t1), '')}</td>
                    <td>{formatAmount(b.amount, '')}</td>
                    {/* win is null while the bet is unsettled / deleted */}
                    <td><span className={signed(win)}>{b.win == null ? '' : formatAmount(win)}</span></td>
                    <td>{fmtDateTime(b.date)}</td>
                    <td>{b.ip}</td>
                    <td>
                      <span
                        className="stmt-detail-link"
                        title={b.browser}
                        onClick={() => setOpenBrowser((cur) => (cur === b.id ? null : b.id))}
                      >Detail</span>
                    </td>
                    <td className="text-end">
                      <input type="checkbox" className="stmt-action-check" title="Toggle Row Selected" checked={picked.has(b.id)} onChange={() => toggle(b.id)} />
                    </td>
                  </tr>,
                  openBrowser === b.id ? (
                    <tr key={`${b.id}-browser`}>
                      <td colSpan={9} className="text-muted">{b.browser || 'No browser details'}</td>
                    </tr>
                  ) : null,
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
